import { useState } from 'react';
import { Input } from 'antd';
import dayjs from 'dayjs';

interface Props {
  /** [起, 迄] 的標示文字（亦作為 aria-label）。 */
  placeholder: [string, string];
  /** 起日取當天 00:00、迄日取當天 23:59:59.999（與桌機 RangePicker 相同）。 */
  onChange: (from?: number, to?: number) => void;
}

const toMs = (v: string, edge: 'start' | 'end'): number | undefined =>
  v ? (edge === 'start' ? dayjs(v).startOf('day') : dayjs(v).endOf('day')).valueOf() : undefined;

/**
 * 手機用的日期區間：兩格原生日期輸入（起／迄）。
 * AntD 的月曆彈窗在矮螢幕（iPhone 15 / SE）上下都放不下會被切掉；
 * 原生 date input 由系統跳出滾輪／月曆，永遠不會超出畫面。各佔 grid 一整列（半格放不下日期）。
 */
export default function DateRangePair({ placeholder, onChange }: Props) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const field = (label: string, value: string, set: (v: string) => void, bound: { min?: string; max?: string }) => (
    <Input
      type="date"
      allowClear
      aria-label={label}
      prefix={<span className="text-xs text-gray-400">{label.includes('起') ? '起' : '迄'}</span>}
      value={value}
      {...bound}
      onChange={(e) => set(e.target.value)}
      className="col-span-2 w-full"
    />
  );

  // iOS Safari 不強制 min/max：起訖顛倒時自動對調，不要默默查出 0 筆
  const emit = (f: string, t: string) =>
    f && t && f > t ? onChange(toMs(t, 'start'), toMs(f, 'end')) : onChange(toMs(f, 'start'), toMs(t, 'end'));

  return (
    <>
      {field(placeholder[0], from, (v) => { setFrom(v); emit(v, to); }, { max: to || undefined })}
      {field(placeholder[1], to, (v) => { setTo(v); emit(from, v); }, { min: from || undefined })}
    </>
  );
}
